import * as React from "react"
import * as CheckboxPrimitive from "@radix-ui/react-checkbox"
import { cva, type VariantProps } from "class-variance-authority"
import { Checkmark, Subtract } from "@carbon/icons-react"
import { cn } from "../../../lib/utils"

const checkboxVariants = cva(
  [
    "peer shrink-0 border-[1.5px] transition-all",
    "focus-visible:outline-none",
    /* disabled unchecked: general/border/solid-30 (--ds-solid-neutral-7) border, white bg, no hover ring */
    "disabled:cursor-not-allowed disabled:border-[var(--ds-solid-neutral-7)] disabled:bg-[var(--ds-white)] disabled:hover:ring-0",
    /* disabled checked/indeterminate: general/molecules-bg/bg-disabled (--ds-solid-neutral-5) */
    "disabled:data-[state=checked]:bg-[var(--ds-solid-neutral-5)] disabled:data-[state=checked]:border-[var(--ds-solid-neutral-5)]",
    "disabled:data-[state=indeterminate]:bg-[var(--ds-solid-neutral-5)] disabled:data-[state=indeterminate]:border-[var(--ds-solid-neutral-5)]",
    "data-[state=checked]:text-[var(--ds-white)] data-[state=indeterminate]:text-[var(--ds-white)]",
  ].join(" "),
  {
    variants: {
      variant: {
        /* border: general/border/solid-30 (--ds-solid-neutral-7) */
        default: [
          "border-[var(--ds-solid-neutral-7)] bg-[var(--ds-white)]",
          "hover:ring-2 hover:ring-offset-1 hover:ring-[var(--ds-solid-neutral-4)]",
          "focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-[var(--ds-solid-neutral-4)]",
        ].join(" "),
        /* border: general/molecules-bg/bg-destructive/dark (--ds-solid-red-600) */
        error: [
          "border-[var(--ds-solid-red-600)] bg-[var(--ds-white)]",
          "hover:ring-2 hover:ring-offset-1 hover:ring-[var(--ds-solid-red-300)]",
          "focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-[var(--ds-solid-red-300)]",
        ].join(" "),
      },
      size: {
        sm: "h-4 w-4 rounded-md",
        md: "h-5 w-5 rounded-md",
        lg: "h-6 w-6 rounded-lg",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "sm",
    },
  }
)

export type CheckedState = boolean | "indeterminate"

export interface CheckboxProps
  extends React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>,
    VariantProps<typeof checkboxVariants> {
  label?: string
  secondaryText?: string
  flipped?: boolean
}

const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  CheckboxProps
>(({ className, variant, size, label, secondaryText, flipped = false, disabled, checked, onCheckedChange, ...props }, ref) => {
  const isChecked = checked === true
  const isIndeterminate = checked === "indeterminate"
  const isActive = isChecked || isIndeterminate

  const activeClasses = (() => {
    if (!isActive) return ""
    /* disabled active: general/molecules-bg/bg-disabled (--ds-solid-neutral-5) */
    if (disabled) return "bg-[var(--ds-solid-neutral-5)] border-[var(--ds-solid-neutral-5)]"
    /* error active: general/molecules-bg/bg-destructive/dark (--ds-solid-red-600) */
    if (variant === "error") return "bg-[var(--ds-solid-red-600)] border-[var(--ds-solid-red-600)]"
    /* default active: general/molecules-bg/bg-neutral (--ds-solid-neutral-10) */
    return "bg-[var(--ds-solid-neutral-10)] border-[var(--ds-solid-neutral-10)]"
  })()

  return (
    <div className={cn("flex items-center gap-2 font-[var(--font-family--sans)]", flipped && "flex-row-reverse justify-between w-full")}>
      <CheckboxPrimitive.Root
        ref={ref}
        disabled={disabled}
        checked={checked}
        onCheckedChange={onCheckedChange}
        className={cn(checkboxVariants({ variant, size }), activeClasses, className)}
        {...props}
      >
        <CheckboxPrimitive.Indicator className="flex items-center justify-center w-full h-full">
          {isIndeterminate ? (
            <Subtract size={10} style={{ fill: 'var(--ds-white)' }} />
          ) : (
            <Checkmark size={10} style={{ fill: 'var(--ds-white)' }} />
          )}
        </CheckboxPrimitive.Indicator>
      </CheckboxPrimitive.Root>

      {(label || secondaryText) && (
        <div className={cn("flex flex-col leading-tight", flipped && "flex-1")}>
          {label && (
            <label
              htmlFor={props.id}
              className={cn(
                "text-sm font-medium text-foreground cursor-pointer select-none",
                disabled && "text-muted-foreground cursor-not-allowed text-xs"
              )}
            >
              {label}
            </label>
          )}
          {secondaryText && (
            <span className={cn("text-xs text-muted-foreground", disabled && "text-muted-foreground/60")}>
              {secondaryText}
            </span>
          )}
        </div>
      )}
    </div>
  )
})
Checkbox.displayName = CheckboxPrimitive.Root.displayName

export interface CheckboxGroupProps {
  /** inline = horizontal flex row; block = vertical flex column (each item full-width) */
  orientation?: "inline" | "block"
  className?: string
  children: React.ReactNode
}

const CheckboxGroup = ({ orientation = "inline", className, children }: CheckboxGroupProps) => (
  <div
    className={cn(
      "flex gap-3",
      orientation === "inline" ? "flex-row flex-wrap items-center" : "flex-col w-full",
      className
    )}
  >
    {children}
  </div>
)
CheckboxGroup.displayName = "CheckboxGroup"

export { Checkbox, checkboxVariants, CheckboxGroup }
